/* eslint-disable max-len */
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ButtonBase } from '@one-thd/sui-atomic-components/dist/private/components/button/ButtonBase';
import createTypography from '@one-thd/sui-atomic-components/dist/utils/createTypography';
import './ExpandableFab.css';

export const ExpandableFab = React.forwardRef((props, ref) => {
  const {
    children,
    component = 'button',
    label = null,
    collapsible = true,
    forceExpand = false,
    ...other
  } = props;

  const { classes: labelTypoClasses, element: LabelElement } = createTypography({
    variant: 'body-lg',
    weight: 'bold',
    height: 'normal',
    color: 'none'
  });

  // CSS can't transitions between width: 0 and width: fit-content so we use max-width instead
  // max-width should start before opacity in both the start hover and end hover transitions.
  // padding should happen quickly in the middle of the max-width transition to not be noticeable.
  // group-hover and group-focus should have same values
  // Transition will appear faster if label does not take up the full max width
  const labelClasses = classNames(labelTypoClasses, {
    'sui-overflow-hidden sui-max-w-0 sui-opacity-0 sui-ml-0 sui-whitespace-nowrap sui-transition-[max-width,opacity] motion-reduce:sui-transition-none sui-delay-[0ms,600ms] group-hover:sui-duration-[1700ms,1500ms] group-focus:sui-duration-[1700ms,1500ms] sui-duration-[1700ms,1500ms] sui-ease-linear group-hover:sui-max-w-64 group-focus:sui-max-w-64 group-hover:sui-opacity-100 group-focus:sui-opacity-100': collapsible,
    'sui-max-w-64 sui-opacity-100': collapsible && forceExpand,
  });

  return (
    <ButtonBase
      className="expandable-fab-button sui-h-11 sui-p-2 sui-rounded-full sui-shadow-md sui-bg-primary sui-text-primary focus-visible:sui-outline-button-active sui-group"
      component={component}
      ref={ref}
      data-component="ExpandableFab"
      // Passthrough wrapper: forward arbitrary button attributes (aria-label, onClick, clickid, …)
      {...other}
    >
      <div className="sui-flex sui-flex-row sui-items-center sui-overflow-hidden">
        {children}
        {label && (
          <LabelElement className={labelClasses}>
            &nbsp;{label}&nbsp;
          </LabelElement>
        )}
      </div>
    </ButtonBase>
  );
});

ExpandableFab.displayName = 'ExpandableFab';

ExpandableFab.propTypes = {
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   */
  component: PropTypes.elementType,
  /**
   * Text to be displayed inside the button.
   */
  label: PropTypes.string,
  /**
   * If true then will look like a normal FAB until hovered or focused on
   * If false will always show label
   */
  collapsible: PropTypes.bool,
  /**
   * Forces the fab to expand without needing to be hovered or focused on.
   * Only works if `expandable === true`
   */
  forceExpand: PropTypes.bool,
};
